import { useState, useEffect, useRef } from 'react';
import { TruVueMark, Btn } from './UI';
import { RandomLetterSwap } from './ui/random-letter-swap';

const NAV_LINKS = [
  ['#problem', 'Problem'],
  ['#platform', 'Platform'],
  ['#how', 'How it Works'],
  ['#why-truvue', 'Why TruVue AI'],
  ['#security', 'Security'],
  ['#gallery', 'Gallery'],
  ['#faq', 'FAQ'],
];

const HEADER_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

  /* ── Floating Pill Wrapper ── */
  .hdr-outer {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    pointer-events: none;
    padding: 18px 20px 0;
    box-sizing: border-box;
  }

  .hdr-pill {
    pointer-events: all;
    max-width: 1100px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    padding: 0 10px 0 18px;
    border-radius: 999px;
    background: rgba(10, 10, 10, 0.88);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.3);
    transition: background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    box-sizing: border-box;
  }

  .hdr-pill.scrolled {
    background: rgba(5, 5, 5, 0.97);
    border-color: rgba(255, 255, 255, 0.15);
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
  }

  /* ── Brand ── */
  .hdr-brand {
    display: flex;
    align-items: center;
    gap: 9px;
    text-decoration: none;
    flex-shrink: 0;
  }

  .hdr-brand-name {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: #0A1628;
    letter-spacing: -0.02em;
  }

  .hdr-brand-badge {
    font-size: 9px;
    font-weight: 700;
    background: #FFF0EB;
    color: #C94B2A;
    border-radius: 5px;
    padding: 2px 6px;
    letter-spacing: 0.06em;
    transform: translateY(-7px);
    text-transform: uppercase;
  }

  /* ── Desktop Nav ── */
  .hdr-nav {
    display: flex;
    align-items: center;
    gap: 0px;
    flex: 1;
    justify-content: center;
    overflow: hidden;
  }

  .hdr-nav-link {
    position: relative;
    padding: 7px 10px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12.5px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.75);
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s ease;
    white-space: nowrap;
    user-select: none;
    background: none;
    border-radius: 0;
  }

  .hdr-nav-link:hover {
    color: #ffffff;
    background: none;
  }

  /* ── Right Controls ── */
  .hdr-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .hdr-cta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 20px;
    border-radius: 999px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    background: linear-gradient(135deg, #C94B2A 0%, #E05C37 100%);
    border: none;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.25s ease;
    white-space: nowrap;
    box-shadow: 0 2px 12px rgba(201, 75, 42, 0.3);
  }

  .hdr-cta:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(201, 75, 42, 0.45);
  }

  /* ── Hamburger ── */
  .hdr-hamburger {
    display: none;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;
    padding: 0;
  }

  .hdr-hamburger:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.35);
  }

  .hdr-hamburger svg {
    width: 18px;
    height: 18px;
    color: #ffffff;
    transition: transform 0.3s ease;
  }

  /* ── Mobile Drawer ── */
  .hdr-drawer-bg {
    position: fixed;
    inset: 0;
    z-index: 9998;
    background: rgba(10, 22, 40, 0.4);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
  }

  .hdr-drawer-bg.open {
    opacity: 1;
    pointer-events: all;
  }

  .hdr-drawer {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 9999;
    width: min(340px, 90vw);
    height: 100dvh;
    background: #0a0a0a;
    box-shadow: -20px 0 60px rgba(9, 30, 66, 0.18);
    display: flex;
    flex-direction: column;
    padding: 24px;
    box-sizing: border-box;
    transform: translateX(100%);
    transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .hdr-drawer.open {
    transform: translateX(0%);
  }

  .hdr-drawer-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 36px;
  }

  .hdr-drawer-close {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(10, 22, 40, 0.06);
    border: none;
    cursor: pointer;
    color: #0A1628;
    transition: background 0.2s ease;
  }

  .hdr-drawer-close:hover {
    background: rgba(10, 22, 40, 0.12);
  }

  .hdr-drawer-links {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }

  .hdr-drawer-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 16px;
    border-radius: 14px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease;
    border: 1px solid transparent;
  }

  .hdr-drawer-link:hover {
    background: rgba(201, 75, 42, 0.12);
    color: #ff7755;
    border-color: rgba(201, 75, 42, 0.25);
  }

  .hdr-drawer-link svg {
    width: 16px;
    height: 16px;
    opacity: 0.4;
  }

  .hdr-drawer-cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    border-radius: 16px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: #ffffff;
    background: linear-gradient(135deg, #C94B2A 0%, #E05C37 100%);
    text-decoration: none;
    margin-top: 16px;
    box-shadow: 0 6px 24px rgba(201, 75, 42, 0.35);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .hdr-drawer-cta:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(201, 75, 42, 0.45);
  }

  .hdr-drawer-footer {
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.3);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    text-align: center;
  }

  /* ── Responsive Breakpoints ── */
  @media (max-width: 960px) {
    .hdr-nav { display: none !important; }
    .hdr-hamburger { display: flex !important; }
    .hdr-cta.desktop-only { display: none !important; }
  }

  @media (min-width: 961px) {
    .hdr-hamburger { display: none !important; }
    .hdr-drawer, .hdr-drawer-bg { display: none !important; }
  }
`;

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const closeDrawer = () => setOpen(false);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: HEADER_STYLES }} />

      {/* Floating pill header */}
      <div className="hdr-outer">
        <div className={`hdr-pill${scrolled ? ' scrolled' : ''}`}>

          {/* Brand */}
          <a href="#top" className="hdr-brand">
            <img src="/logo.png" alt="TruVue AI" style={{ height: 56, width: 'auto', display: 'block' }} />
          </a>

          {/* Desktop nav */}
          <nav className="hdr-nav">
            {NAV_LINKS.map(([href, label]) => (
              <a key={href} href={href} className="hdr-nav-link">
                <RandomLetterSwap label={label} staggerDuration={0.022} />
              </a>
            ))}
          </nav>

          {/* Right: CTA + hamburger */}
          <div className="hdr-right">
            <a href="https://wa.me/919425074535" target="_blank" rel="noopener noreferrer" className="hdr-cta desktop-only">
              Book a Demo →
            </a>

            {/* Hamburger (mobile only) */}
            <button
              className="hdr-hamburger"
              onClick={() => setOpen(o => !o)}
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              {open ? (
                <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <line x1="3" y1="3" x2="21" y2="21" />
                  <line x1="21" y1="3" x2="3" y2="21" />
                </svg>
              ) : (
                <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer backdrop */}
      <div
        className={`hdr-drawer-bg${open ? ' open' : ''}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* Mobile drawer panel */}
      <div className={`hdr-drawer${open ? ' open' : ''}`} role="dialog" aria-modal="true">
        <div className="hdr-drawer-top">
          <a href="#top" className="hdr-brand" onClick={closeDrawer}>
            <img src="/logo.png" alt="TruVue AI" style={{ height: 36, width: 'auto', display: 'block' }} />
          </a>
          <button className="hdr-drawer-close" onClick={closeDrawer} aria-label="Close menu">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <line x1="3" y1="3" x2="21" y2="21" />
              <line x1="21" y1="3" x2="3" y2="21" />
            </svg>
          </button>
        </div>

        <nav className="hdr-drawer-links">
          {NAV_LINKS.map(([href, label]) => (
            <a
              key={href}
              href={href}
              className="hdr-drawer-link"
              onClick={closeDrawer}
            >
              {label}
              <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          ))}
        </nav>

        <a href="https://wa.me/919425074535" target="_blank" rel="noopener noreferrer" className="hdr-drawer-cta" onClick={closeDrawer}>
          Book a Demo →
        </a>

        <p className="hdr-drawer-footer">
          © 2026 TruVue AI · AI Industrial Safety
        </p>
      </div>
    </>
  );
}
